import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Menu as MenuIcon, Play, X } from 'lucide-react';
import { Link } from 'react-router-dom';
const columns = [{
  id: 'corporate',
  title: 'Corporate',
  video: 'https://assets.mixkit.co/videos/preview/mixkit-chef-preparing-a-dish-in-a-professional-kitchen-40811-large.mp4',
  poster: 'https://images.unsplash.com/photo-1544025162-d76694265947?auto=format&fit=crop&q=80&w=1200'
}, {
  id: 'weddings',
  title: 'Weddings',
  video: 'https://cdn.magicpatterns.com/uploads/wf4z4ppJqTDJXxtz61HQ7y/Graduation Catering Party.mov',
  poster: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=1200'
}, {
  id: 'meal-prep',
  title: 'Meal Prep',
  video: 'https://assets.mixkit.co/videos/preview/mixkit-fresh-vegetables-being-chopped-on-a-wooden-board-40815-large.mp4',
  poster: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?auto=format&fit=crop&q=80&w=1200'
}];
interface HeroProps {
  onOpenQuote: () => void;
}
export function Hero({
  onOpenQuote
}: HeroProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navLinks = [{
    name: 'Home',
    path: '/'
  }, {
    name: 'Services',
    path: '/services'
  }, {
    name: 'Menu',
    path: '/menu'
  }, {
    name: 'Story',
    path: '/story'
  }, {
    name: 'Contact',
    path: '/contact'
  }];
  return <section className="relative h-[85vh] md:h-[90vh] w-full overflow-hidden flex flex-col md:flex-row bg-brand-black" data-id="element-115">
      {/* Top Navigation Bar */}
      <div className="absolute top-0 left-0 w-full z-50 flex flex-col px-4 md:px-8 py-4 md:py-6" data-id="element-116">
        <div className="flex items-center justify-between bg-brand-black/40 backdrop-blur-sm px-4 md:px-8 py-3 md:py-4 rounded-sm border border-white/10 w-full" data-id="element-117">
          <div className="flex items-center" data-id="element-118">
            <img src="https://cdn.magicpatterns.com/uploads/qTzjp2dzSu8XKjmJQNf3Qh/Catering_Logo.png" alt="Versatile LLC" className="h-12 md:h-20 w-auto object-contain mr-4 md:mr-12" data-id="element-119" />
            <nav className="hidden xl:flex items-center gap-10" data-id="element-120">
              {navLinks.map(link => <Link key={link.name} to={link.path} className="text-[11px] font-bold uppercase tracking-[0.2em] text-white hover:text-brand-gold transition-colors" data-id="element-121">
                  {link.name}
                </Link>)}
            </nav>
          </div>

          <div className="flex items-center gap-4 md:gap-8" data-id="element-122">
            <button className="text-white hover:text-brand-gold transition-colors relative" data-id="element-123">
              <ShoppingCart size={24} className="md:w-7 md:h-7" data-id="element-124" />
              <span className="absolute -top-2 -right-2 bg-brand-gold text-white text-[10px] w-4 h-4 md:w-5 md:h-5 rounded-full flex items-center justify-center font-bold" data-id="element-125">
                0
              </span>
            </button>
            <button onClick={onOpenQuote} className="hidden xl:block bg-brand-gold text-white px-10 py-6 rounded-sm text-xs font-bold uppercase tracking-widest hover:bg-white hover:text-brand-black transition-all shadow-2xl leading-tight text-center" data-id="element-126">
              Request a Custom <br data-id="element-127" /> Quote
            </button>
            <button className="xl:hidden text-white p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} data-id="element-128">
              {isMobileMenuOpen ? <X size={24} data-id="element-129" /> : <MenuIcon size={24} data-id="element-130" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-131">
          {isMobileMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="xl:hidden mt-2 bg-brand-black/95 backdrop-blur-md border border-white/10 rounded-sm p-4 flex flex-col gap-4 w-full" data-id="element-132">
              {navLinks.map(link => <Link key={link.name} to={link.path} onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-bold uppercase tracking-[0.2em] text-white hover:text-brand-gold transition-colors py-2 border-b border-white/10" data-id="element-133">
                  {link.name}
                </Link>)}
              <button onClick={() => {
            onOpenQuote();
            setIsMobileMenuOpen(false);
          }} className="bg-brand-gold text-white px-4 py-4 rounded-sm text-xs font-bold uppercase tracking-widest w-full mt-2" data-id="element-134">
                Request a Quote
              </button>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* 3-Column Background Grid with Video */}
      <div className="absolute inset-0 flex flex-col md:flex-row z-0" data-id="element-135">
        {columns.map(col => <motion.div key={col.id} className="relative flex-1 h-full overflow-hidden border-r border-white/5 last:border-r-0 group" whileHover={{
        flex: 1.5
      }} transition={{
        duration: 0.6,
        ease: 'circOut'
      }} data-id="element-136">
            <div className="absolute inset-0" data-id="element-137">
              <video autoPlay muted loop playsInline poster={col.poster} className="w-full h-full object-cover opacity-40 grayscale group-hover:grayscale-0 group-hover:opacity-70 transition-all duration-700" data-id="element-138">
                <source src={col.video} type="video/mp4" data-id="element-139" />
              </video>
              <div className="absolute inset-0 bg-brand-black/20 group-hover:bg-transparent transition-colors duration-700" data-id="element-140" />
            </div>

            <div className="absolute bottom-12 left-0 w-full text-center pointer-events-none" data-id="element-141">
              <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-white/40 group-hover:text-brand-gold transition-colors" data-id="element-142">
                {col.title}
              </span>
            </div>
          </motion.div>)}
      </div>

      {/* Centered Content Overlay */}
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4 md:px-6 pointer-events-none" data-id="element-143">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-144">
          <h1 className="text-white text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif leading-tight mb-6 md:mb-8" data-id="element-145">
            DMV Catering <br data-id="element-146" />
            <span className="text-brand-gold italic" data-id="element-147">for Any Event.</span>
          </h1>

          <motion.button onClick={onOpenQuote} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="pointer-events-auto bg-brand-gold text-white px-8 py-4 md:px-12 md:py-6 rounded-sm text-xs md:text-sm font-bold uppercase tracking-[0.2em] md:tracking-[0.3em] hover:bg-white hover:text-brand-black transition-all duration-300 shadow-2xl" data-id="element-148">
            Book Your Event
          </motion.button>
        </motion.div>
      </div>

      {/* Bottom Gradient for Transition */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-brand-white to-transparent z-20" data-id="element-149" />
    </section>;
}